import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Box,
  Container,
  Typography,
  CircularProgress,
  Alert,
  Breadcrumbs,
  Link,
  Button,
  useTheme,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import CourseSidebar from '../components/LessonSidebar';
import CourseOverview from '../components/CourseOverview';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { getObject, getSignedUrl } from '.././api/s3Api';
import { useAuth0 } from '@auth0/auth0-react';
import { useOrgUser } from '../context/OrgUserProvider';

const S3_BASE_URL = 'https://qallix-public.s3.ap-southeast-1.amazonaws.com/QallixAcademy/SoftwareCourses';
const PUBLIC_BASE_URL = 'https://qallix-public.s3.ap-southeast-1.amazonaws.com';

// Cache control headers for fetch requests
const fetchOptions = {
  headers: {
    'Cache-Control': 'no-cache, no-store, must-revalidate',
    'Pragma': 'no-cache',
    'Expires': '0'
  }
};

const LessonContent = ({
  title,
  isLast,
  children,
  courseId
}) => {
  const [expanded, setExpanded] = useState(false);
  const [completed, setCompleted] = useState(false);

  const storageKey = `lesson-completed-${courseId}-${title.replace(/\s+/g, '-').toLowerCase()}`;

  useEffect(() => {
    const isCompleted = localStorage.getItem(storageKey) === 'true';
    if (isCompleted) {
      setCompleted(true);
    }
  }, [storageKey]);

  const handleExpand = () => {
    const newExpanded = !expanded;
    setExpanded(newExpanded);

    if (newExpanded && !completed) {
      setCompleted(true);
      localStorage.setItem(storageKey, 'true');
    }
  };

  return (
    <Box>
      <Accordion
        expanded={expanded}
        onChange={handleExpand}
        elevation={0}
        sx={{
          border: '1px solid #e2e2e2ff',
          mb: isLast ? '10px' : '0px'
        }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          sx={{
            minHeight: '56px !important',
            '& .MuiAccordionSummary-content': { margin: '12px 0' }
          }}
        >
          {completed ? (
            <CheckCircleIcon
              color="success"
              sx={{
                fontSize: '18px',
                mr: 1.5,
                mt: 0.5
              }}
            />
          ) : (
            <Box sx={{
              width: '10px',
              height: '10px',
              borderRadius: '50%',
              background: '#ebebeb',
              mr: '12px',
              mt: '8px'
            }} />
          )}
          <Typography variant="subtitle1" fontWeight={500}>
            {title}
          </Typography>
        </AccordionSummary>

        <AccordionDetails sx={{ pt: 0 }}>
          <Divider sx={{ mb: 2 }} />
          <Box sx={{
            '& > *': { mb: 2 },
            '& > *:last-child': { mb: 0 }
          }}>
            {children}
          </Box>
        </AccordionDetails>
      </Accordion>
    </Box>
  );
};

const CourseContentRenderer = ({ content, courseId, isPublicCourse = false }) => {
  const { getAccessTokenSilently } = useAuth0();
  const { orgUser } = useOrgUser();
  const [mediaUrls, setMediaUrls] = useState({});
  const [loadingMedia, setLoadingMedia] = useState(true);

  useEffect(() => {
    const fetchMediaUrls = async () => {
      if (!content) return;

      try {
        // For public courses, use direct public URLs
        if (isPublicCourse) {
          const urls = {};
          content
            .filter(item =>
              (item.type === 'video' || item.type === 'image') &&
              !isExternalUrl(item.url) && // Exclude external URLs
              !isYouTubeUrl(item.url)     // Exclude YouTube URLs
            )
            .forEach(item => {
              // Use the direct public URL for public course media
              urls[item.imageKey || item.url] = `${PUBLIC_BASE_URL}/${item.imageKey || item.url}`;
            });

          setMediaUrls(urls);
          setLoadingMedia(false);
          return;
        }

        // For non-public courses, use the signed URL approach
        const token = await getAccessTokenSilently({
          audience: 'https://qallix-api',
        });

        const orgName = orgUser?.orgName;
        if (!orgName) return;

        // Find only S3 media items (exclude YouTube URLs and external URLs)
        const mediaItems = content.filter(item =>
          (item.type === 'video' || item.type === 'image') &&
          !isExternalUrl(item.url) && // Exclude external URLs
          !isYouTubeUrl(item.url)     // Exclude YouTube URLs
        ).map(item => ({
          bucketName: 'qallix-storage',
          key: item.imageKey || item.url
        }));

        if (mediaItems.length === 0) {
          setLoadingMedia(false);
          return;
        }

        const payload = {
          organization: orgName,
          draftItems: mediaItems
        };

        const response = await getSignedUrl(token, payload);

        if (response.statusCode === 200 && response.data) {
          const urls = {};
          mediaItems.forEach((item, index) => {
            if (response.data[index]) {
              urls[item.key] = response.data[index];
            }
          });
          setMediaUrls(urls);
        }
      } catch (error) {
        console.error('Error fetching signed URLs:', error);
      } finally {
        setLoadingMedia(false);
      }
    };

    fetchMediaUrls();
  }, [content, getAccessTokenSilently, orgUser, isPublicCourse]);

  // Helper functions to check URL types
  const isYouTubeUrl = (url) => {
    if (!url) return false;
    return url.includes('youtube.com') ||
      url.includes('youtu.be') ||
      url.includes('youtube-embed') ||
      (typeof url === 'string' && url.trim().startsWith('<iframe'));
  };

  const isExternalUrl = (url) => {
    if (!url) return false;
    return url.startsWith('http://') ||
      url.startsWith('https://') ||
      url.startsWith('www.');
  };

  const isIframeEmbed = (url) => {
    if (!url) return false;
    return typeof url === 'string' && url.trim().startsWith('<iframe');
  };

  const extractYouTubeId = (url) => {
    if (isIframeEmbed(url)) {
      // Extract YouTube ID from iframe embed code
      const match = url.match(/youtube\.com\/embed\/([^?"]+)/) ||
        url.match(/youtube\.com\/watch\?v=([^&"]+)/) ||
        url.match(/youtu\.be\/([^?"]+)/);
      return match ? match[1] : null;
    } else {
      // Extract from regular YouTube URL
      const match = url.match(/youtube\.com\/watch\?v=([^&]+)/) ||
        url.match(/youtube\.com\/embed\/([^?]+)/) ||
        url.match(/youtu\.be\/([^?]+)/);
      return match ? match[1] : null;
    }
  };

  if (!content) return null;

  // Show loading state while fetching media URLs
  if (loadingMedia) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return content.map((item, index) => {
    switch (item.type) {
      case 'text':
        return (
          <Box key={index} sx={{ mb: 2 }}>
            <div dangerouslySetInnerHTML={{ __html: item.value }} />
          </Box>
        );
      case 'video':
        if (isYouTubeUrl(item.url)) {
          const videoId = extractYouTubeId(item.url);

          return (
            <Box key={index} sx={{
              width: { xs: '100%', md: item.width || '75%' },
              my: 4,
              mx: 'auto'
            }}>
              <Box sx={{
                width: '100%',
                height: { xs: '250px', sm: '350px', md: '450px', lg: '500px' }, // Responsive heights
                borderRadius: '8px',
                overflow: 'hidden',
                backgroundColor: '#000'
              }}>
                {videoId ? (
                  <iframe
                    src={`https://www.youtube.com/embed/${videoId}`}
                    style={{
                      width: '100%',
                      height: '100%',
                      border: 'none'
                    }}
                    allowFullScreen
                    title="YouTube video player"
                  />
                ) : (
                  <div
                    style={{
                      width: '100%',
                      height: '100%'
                    }}
                    dangerouslySetInnerHTML={{ __html: item.url }}
                  />
                )}
              </Box>
              {item.caption && (
                <Typography variant="caption" sx={{ display: 'block', mt: 1, textAlign: 'center' }}>
                  {item.caption}
                </Typography>
              )}
            </Box>
          );
        }

        // Regular S3 video
        const videoUrl = mediaUrls[item.imageKey] || mediaUrls[item.url];
        return (
          <Box key={index} sx={{
            width: { xs: '100%', md: item.width || '75%' },
            position: 'relative',
            paddingBottom: { xs: '56.25%', md: '42.1875%' },
            height: 0,
            overflow: 'hidden',
            my: 4
          }}>
            {videoUrl ? (
              <video
                width="100%"
                height="100%"
                style={{ position: 'absolute', top: 0, left: 0 }}
                controls
                autoPlay={false}
                muted
              >
                <source src={videoUrl} type="video/mp4" />
                Your browser does not support the video tag.
              </video>
            ) : (
              <Box sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: '#f5f5f5'
              }}>
                <Typography>Video not available</Typography>
              </Box>
            )}
            {item.caption && (
              <Typography variant="caption" sx={{ display: 'block', mt: 1, textAlign: 'center' }}>
                {item.caption}
              </Typography>
            )}
          </Box>
        );
      case 'image':
        // Check if it's an external image URL
        if (isExternalUrl(item.url) && !isYouTubeUrl(item.url)) {
          return (
            <Box key={index} display="flex" justifyContent="center" mb={3} sx={{
              width: { xs: '100%', md: item.width || '80%' }
            }}>
              <img
                src={item.url}
                alt={item.alt || ''}
                style={{
                  width: '100%',
                  borderRadius: 8,
                  border: '1px solid #e0e0e0'
                }}
                onError={(e) => {
                  e.target.style.display = 'none';
                }}
              />
              {item.caption && (
                <Typography variant="caption" sx={{ display: 'block', mt: 1, textAlign: 'center' }}>
                  {item.caption}
                </Typography>
              )}
            </Box>
          );
        }

        // S3 image
        const imageUrl = mediaUrls[item.imageKey] || mediaUrls[item.url];
        return (
          <Box key={index} display="flex" justifyContent="center" mb={3} sx={{
            width: { xs: '100%', md: item.width || '80%' }
          }}>
            {imageUrl ? (
              <img
                src={imageUrl}
                alt={item.alt || ''}
                style={{
                  width: '100%',
                  borderRadius: 8,
                  border: '1px solid #e0e0e0'
                }}
              />
            ) : (
              <Box sx={{
                width: '100%',
                height: '200px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: '#f5f5f5',
                borderRadius: 8,
                border: '1px solid #e0e0e0'
              }}>
                <Typography>Image not available</Typography>
              </Box>
            )}
            {item.caption && (
              <Typography variant="caption" sx={{ display: 'block', mt: 1, textAlign: 'center' }}>
                {item.caption}
              </Typography>
            )}
          </Box>
        );
      case 'dropdown':
        return (
          <LessonContent
            key={index}
            title={item.title}
            courseId={courseId}
            isLast={index === content.length - 1}
          >
            <CourseContentRenderer
              content={item.content}
              courseId={courseId}
              isPublicCourse={isPublicCourse}
            />
          </LessonContent>
        );
      case 'note':
        return (
          <Alert
            key={index}
            severity="warning"
            icon={<WarningAmberIcon />}
            sx={{
              backgroundColor: '#FFF8E1',
              color: '#5D4037',
              borderRadius: 1,
              fontSize: '0.95rem',
              mb: 2
            }}
          >
            <Typography fontWeight="bold" mb={1}>{item.title}</Typography>
            {item.content}
          </Alert>
        );
      default:
        return null;
    }
  });
};

const Course = () => {
  const { getAccessTokenSilently } = useAuth0();
  const { orgUser } = useOrgUser();
  const { slug: paramSlug } = useParams();
  const [courseManifest, setCourseManifest] = useState(null);
  const navigate = useNavigate();
  const theme = useTheme();
  const [courseData, setCourseData] = useState(null);
  const [lessonData, setLessonData] = useState(null);
  const [activeLesson, setActiveLesson] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lessonError, setLessonError] = useState(null);
  const [cacheBuster, setCacheBuster] = useState(0);
  const slug = paramSlug === undefined || paramSlug === null
    ? "getting-started-with-qallix"
    : paramSlug;

  useEffect(() => {
    const fetchCourseData = async () => {
      try {
        setLoading(true);

        // Add cache-busting parameter to prevent browser caching
        const cacheBusterParam = `?v=${Date.now()}`;

        // First fetch the course manifest
        const manifestRes = await fetch(
          'https://qallix-public.s3.ap-southeast-1.amazonaws.com/QallixAcademy/SoftwareCourses/course-manifest.json' + cacheBusterParam,
          fetchOptions
        );

        if (!manifestRes.ok) {
          throw new Error(`Failed to fetch course manifest: ${manifestRes.status}`);
        }

        const manifestData = await manifestRes.json();
        setCourseManifest(manifestData);

        // Find the course with matching slug
        const courseInfo = manifestData.courses.find(
          course => course.slug === slug && !course.isDeleted
        );

        if (!courseInfo) {
          throw new Error('Course not found');
        }

        // Fetch course data using the ID from manifest
        const courseRes = await fetch(
          `${S3_BASE_URL}/${courseInfo.id}/course-info.json${cacheBusterParam}`,
          fetchOptions
        );

        if (!courseRes.ok) {
          throw new Error(`Failed to fetch course data: ${courseRes.status}`);
        }

        const courseData = await courseRes.json();

        // The lessons are now included in the course-info.json
        setCourseData(courseData);
      } catch (err) {
        setError(err.message || 'Failed to load course data');
      } finally {
        setLoading(false);
      }
    };

    fetchCourseData();
  }, [slug, cacheBuster]);

  useEffect(() => {
    const fetchLessonContent = async () => {
      if (activeLesson && courseData) {
        setLoading(true);
        setLessonError(null);

        // Find the lesson by ID to get its details
        const lesson = courseData.lessons.find(l => l.id === activeLesson);

        if (!lesson) {
          setLessonError({
            message: 'Lesson not found in course data',
            lessonId: activeLesson
          });
          setLoading(false);
          return;
        }

        try {
          // Check if this is the "getting-started-with-qallix" course
          const isPublicCourse = slug === 'getting-started-with-qallix';

          if (isPublicCourse) {
            // For public course, fetch directly from public bucket
            const cacheBusterParam = `?v=${Date.now()}`;
            const lessonRes = await fetch(
              `${PUBLIC_BASE_URL}/QallixAcademy/SoftwareCourses/${courseData.id}/lessons/${lesson.id}/content.json${cacheBusterParam}`,
              fetchOptions
            );

            if (!lessonRes.ok) {
              throw new Error(`Failed to fetch lesson content: ${lessonRes.status}`);
            }

            const lessonContent = await lessonRes.json();

            // Process the content for the new format
            const processedData = {
              ...lessonContent,
              content: lessonContent.map(item => ({
                ...item,
              }))
            };

            setLessonData(processedData);
          } else {
            // For non-public courses, use the original approach with signed URLs
            const token = await getAccessTokenSilently({
              audience: 'https://qallix-api',
            });

            const orgName = orgUser?.orgName;

            if (!orgName) {
              throw new Error('Organization not found');
            }

            // Construct the S3 key path
            const s3Key = `QallixAcademy/SoftwareCourses/${courseData.id}/lessons/${lesson.id}/content.json`;

            // Prepare the payload for the API call
            const payload = {
              organization: orgName,
              bucketName: "qallix-storage",
              key: s3Key
            };

            // Use the getObject function from s3api.js
            const response = await getObject(token, payload);

            if (response.statusCode === 200 && response.fileContent) {
              // Decode the base64 content
              const decodedContent = atob(response.fileContent);
              const lessonContent = JSON.parse(decodedContent);

              // Process the content for the new format
              const processedData = {
                ...lessonContent,
                content: lessonContent.map(item => ({
                  ...item,
                }))
              };

              setLessonData(processedData);
            } else {
              throw new Error(response.error || 'Failed to fetch lesson content');
            }
          }
        } catch (err) {
          console.error('Error fetching lesson:', err);
          setLessonError({
            message: 'This lesson is not available yet',
            status: err.response?.status || 500,
            lessonId: activeLesson
          });
          setLessonData(null);
        } finally {
          setLoading(false);
        }
      }
    };

    fetchLessonContent();
  }, [activeLesson, courseData, getAccessTokenSilently, orgUser, slug]);

  const refreshCourseData = () => {
    setCacheBuster(prev => prev + 1);
    setCourseData(null);
    setActiveLesson(null);
    setLessonData(null);
  };

  const goToNextLesson = () => {
    const currentIndex = courseData.lessons.findIndex(lesson => lesson.id === activeLesson);
    if (currentIndex < courseData.lessons.length - 1) {
      setActiveLesson(courseData.lessons[currentIndex + 1].id);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const goToPrevLesson = () => {
    const currentIndex = courseData.lessons.findIndex(lesson => lesson.id === activeLesson);
    if (currentIndex > 0) {
      setActiveLesson(courseData.lessons[currentIndex - 1].id);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleReturnToOverview = () => {
    setActiveLesson(null);
    setLessonError(null);
  };

  const handleTryAvailableLesson = () => {
    // Find the first available lesson before the errored one
    const errorIndex = courseData.lessons.findIndex(lesson => lesson.id === lessonError.lessonId);
    if (errorIndex > 0) {
      setActiveLesson(courseData.lessons[errorIndex - 1].id);
    } else {
      handleReturnToOverview();
    }
  };

  if (loading && !courseData) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <Alert severity="error" action={
          <Button color="inherit" size="small" onClick={refreshCourseData}>
            Retry
          </Button>
        }>
          {error}
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%' }}>
      <Box>
        <Container maxWidth="xl">
          <Box sx={{
            display: 'flex',
            [theme.breakpoints.down('md')]: {
              flexDirection: 'column'
            }
          }}>
            <CourseSidebar
              course={courseData}
              activeLesson={activeLesson}
              onSelectLesson={setActiveLesson}
              onHeaderClick={() => setActiveLesson(null)}
              errorLesson={lessonError?.lessonId}
            />

            <Box sx={{
              padding: { xs: '20px', md: '40px' },
              width: { md: '80%' },
              flexGrow: 1
            }}>
              {!activeLesson ? (
                <CourseOverview
                  courseId={courseData.id}
                  title={courseData.title}
                  level={courseData.level}
                  lessons={courseData.noOflessons}
                  duration={courseData.duration}
                  image={`${S3_BASE_URL}/${courseData.id}/${courseData.thumbnail}?v=${Date.now()}`}
                  description={courseData.description}
                  onStartCourse={() => setActiveLesson(courseData.lessons[0].id)}
                  onRefresh={refreshCourseData}
                />
              ) : lessonError ? (
                <Box id="lesson-error" sx={{ textAlign: 'center', mt: 4 }}>
                  <ErrorOutlineIcon sx={{ fontSize: 60, color: 'error.main', mb: 2 }} />
                  <Typography variant="h5" gutterBottom>
                    Lesson Not Available
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 3 }}>
                    {lessonError.message}
                  </Typography>

                  <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2 }}>
                    <Button
                      variant="contained"
                      onClick={handleTryAvailableLesson}
                      sx={{
                        mr: 2,
                        backgroundColor: '#000'
                      }}
                    >
                      Go to Last Available Lesson
                    </Button>
                    <Button
                      variant="outlined"
                      onClick={handleReturnToOverview}
                      sx={{
                        borderColor: '#000',
                        color: '#000'
                      }}
                    >
                      Return to Course Overview
                    </Button>
                  </Box>
                </Box>
              ) : loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <CircularProgress />
                </Box>
              ) : (
                <Box id="lesson-content">
                  <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} aria-label="breadcrumb" sx={{ mb: 2 }}>
                    <Link
                      underline="hover"
                      color="inherit"
                      href="/courses"
                    >
                      Courses
                    </Link>
                    <Link
                      underline="hover"
                      color="inherit"
                      href={`/courses/${slug}`}
                      onClick={() => setActiveLesson(null)}
                      sx={{ cursor: 'pointer' }}
                    >
                      {courseData.title}
                    </Link>
                    <Typography color="text.primary">
                      {lessonData?.title}
                    </Typography>
                  </Breadcrumbs>

                  <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 3 }}>
                    {lessonData?.title}
                  </Typography>

                  {lessonData && (
                    <CourseContentRenderer
                      content={lessonData.content}
                      courseId={courseData.id}
                      isPublicCourse={slug === 'getting-started-with-qallix'}
                    />
                  )}

                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
                    <Button
                      sx={{
                        borderColor: '#332872',
                        color: '#332872',
                        '&:hover': {
                          backgroundColor: '#332872',
                          color: '#fff',
                          borderColor: '#332872'
                        }
                      }}
                      variant="outlined"
                      onClick={goToPrevLesson}
                      disabled={activeLesson === courseData.lessons[0].id}
                    >
                      Previous Lesson
                    </Button>
                    <Button
                      sx={{
                        backgroundColor: '#332872',
                        color: '#fff',
                        '&:hover': { backgroundColor: '#2a2160', }
                      }}
                      variant="contained"
                      onClick={goToNextLesson}
                      disabled={activeLesson === courseData.lessons[courseData.lessons.length - 1].id}
                    >
                      Next Lesson
                    </Button>
                  </Box>
                </Box>
              )}
            </Box>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default Course;